import apiClient, { ApiResponse, PaginatedResponse } from '../api/client';
import { Subcounty, Ward, Zone } from '../types/models';

export interface CreateSubcountyData {
  name: string;
  code: string;
}

export interface CreateWardData {
  name: string;
  code: string;
  subcounty_id: number;
}

export interface CreateZoneData {
  name: string;
  code: string;
  ward_id: number;
  module_id: number;
}

export interface LocationFilters {
  search?: string;
  page?: number;
  per_page?: number;
}

class LocationService {
  // County-wide subcounty endpoints
  async getAllSubcounties(filters?: LocationFilters): Promise<ApiResponse<PaginatedResponse<Subcounty>>> {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());

    return apiClient.get<PaginatedResponse<Subcounty>>(`/admin/county/subcounties?${params.toString()}`);
  }

  async createCountySubcounty(data: { name: string }): Promise<ApiResponse<Subcounty>> {
    return apiClient.post<Subcounty>('/admin/county/subcounties', data);
  }

  async updateCountySubcounty(id: number, data: { name?: string }): Promise<ApiResponse<Subcounty>> {
    return apiClient.put<Subcounty>(`/admin/county/subcounties/${id}`, data);
  }

  async deleteCountySubcounty(id: number): Promise<ApiResponse> {
    return apiClient.delete(`/admin/county/subcounties/${id}`);
  }

  // Module-specific subcounty endpoints
  async getSubcounties(moduleId: number, page?: number): Promise<ApiResponse<PaginatedResponse<Subcounty>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<Subcounty>>(`/county/modules/${moduleId}/subcounties${params}`);
  }

  async getSubcounty(moduleId: number, id: number): Promise<ApiResponse<Subcounty>> {
    return apiClient.get<Subcounty>(`/county/modules/${moduleId}/subcounties/${id}`);
  }

  async createSubcounty(moduleId: number, data: CreateSubcountyData): Promise<ApiResponse<Subcounty>> {
    return apiClient.post<Subcounty>(`/county/modules/${moduleId}/subcounties`, data);
  }

  async updateSubcounty(moduleId: number, id: number, data: Partial<CreateSubcountyData>): Promise<ApiResponse<Subcounty>> {
    return apiClient.put<Subcounty>(`/county/modules/${moduleId}/subcounties/${id}`, data);
  }

  async deleteSubcounty(moduleId: number, id: number): Promise<ApiResponse> {
    return apiClient.delete(`/county/modules/${moduleId}/subcounties/${id}`);
  }

  // County-wide ward endpoints
  async getAllWards(filters?: LocationFilters & { subcounty_id?: string }): Promise<ApiResponse<PaginatedResponse<Ward>>> {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());
    if (filters?.subcounty_id) params.append('subcounty_id', filters.subcounty_id);

    return apiClient.get<PaginatedResponse<Ward>>(`/admin/county/wards?${params.toString()}`);
  }

  async createCountyWard(data: { name: string; subcounty_id: number | string }): Promise<ApiResponse<Ward>> {
    return apiClient.post<Ward>('/admin/county/wards', data);
  }

  async updateCountyWard(id: number, data: { name?: string; subcounty_id?: number | string }): Promise<ApiResponse<Ward>> {
    return apiClient.put<Ward>(`/admin/county/wards/${id}`, data);
  }

  async deleteCountyWard(id: number): Promise<ApiResponse> {
    return apiClient.delete(`/admin/county/wards/${id}`);
  }

  // Module-specific ward endpoints
  async getWards(moduleId: number, page?: number): Promise<ApiResponse<PaginatedResponse<Ward>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<Ward>>(`/county/modules/${moduleId}/wards${params}`);
  }

  async getWard(moduleId: number, id: number): Promise<ApiResponse<Ward>> {
    return apiClient.get<Ward>(`/county/modules/${moduleId}/wards/${id}`);
  }

  async createWard(moduleId: number, data: CreateWardData): Promise<ApiResponse<Ward>> {
    return apiClient.post<Ward>(`/county/modules/${moduleId}/wards`, data);
  }

  async updateWard(moduleId: number, id: number, data: Partial<CreateWardData>): Promise<ApiResponse<Ward>> {
    return apiClient.put<Ward>(`/county/modules/${moduleId}/wards/${id}`, data);
  }

  async deleteWard(moduleId: number, id: number): Promise<ApiResponse> {
    return apiClient.delete(`/county/modules/${moduleId}/wards/${id}`);
  }

  // County-wide zone endpoints
  async getAllZones(filters?: LocationFilters & { ward_id?: string }): Promise<ApiResponse<PaginatedResponse<Zone>>> {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());
    if (filters?.ward_id) params.append('ward_id', filters.ward_id);

    return apiClient.get<PaginatedResponse<Zone>>(`/admin/county/zones?${params.toString()}`);
  }

  // Module-specific zone endpoints
  async getZones(moduleId: number, page?: number): Promise<ApiResponse<PaginatedResponse<Zone>>> {
    const params = page ? `?page=${page}` : '';
    return apiClient.get<PaginatedResponse<Zone>>(`/county/modules/${moduleId}/zones${params}`);
  }

  async getZone(moduleId: number, id: number): Promise<ApiResponse<Zone>> {
    return apiClient.get<Zone>(`/county/modules/${moduleId}/zones/${id}`);
  }

  async createZone(moduleId: number, data: CreateZoneData): Promise<ApiResponse<Zone>> {
    return apiClient.post<Zone>(`/county/modules/${moduleId}/zones`, data);
  }

  async updateZone(moduleId: number, id: number, data: Partial<CreateZoneData>): Promise<ApiResponse<Zone>> {
    return apiClient.put<Zone>(`/county/modules/${moduleId}/zones/${id}`, data);
  }

  async deleteZone(moduleId: number, id: number): Promise<ApiResponse> {
    return apiClient.delete(`/county/modules/${moduleId}/zones/${id}`);
  }
}

export const locationService = new LocationService();
export default locationService;
